/**
 * SASADM-0156 — the brand editor's new image slots (saas-auth SASATH-0347,
 * #962 §3.5 / §4.3), rendered under the existing images in the Advanced tab
 * only when `env.brandAssetSlots` is on.
 *
 * - Current values come from the theme GET's `resolved.assets` (contract §3).
 * - Each slot shows its rules, a light and a dark preview tile, Replace (pick a
 *   file → `POST …/assets/{slot}`) and Clear (confirm → `DELETE`).
 * - A 200 shows the returned, content-addressed `url` straight away; either
 *   action calls `onChanged` so the page marks the Theme tab stale.
 * - Errors show the server's `message` as-is (an `SVG_REJECTED` adds its
 *   `reason`); a 413 / `ASSET_TOO_LARGE` shows the size message.
 * - SASADM-0160: a file over 5 MB is refused client-side with no request — an
 *   oversized multipart body can get a 500 instead of the 413 (0347 F3).
 * - SASADM-0161: a 429 on upload or clear shows the `Retry-After` wait, like
 *   the other brand panels.
 */
import { useEffect, useState, type ChangeEvent } from 'react';
import {
  Alert,
  alpha,
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { apiAdmin } from '@/api/admin';
import { DEFAULT_RETRY_AFTER_SECONDS } from '@/api/client';
import { ApiError } from '@/api/errors';
import type { BrandAssetSlot, ResolvedAssets } from '@/api/types';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useCopy } from '@/hooks/useCopy';

type SlotUrls = Record<BrandAssetSlot, string | null>;

const GROUPS: ReadonlyArray<{ key: string; label: string; slots: BrandAssetSlot[] }> = [
  { key: 'marks', label: 'Marks', slots: ['logoReversed', 'mark', 'faviconSvg'] },
  { key: 'appIcons', label: 'App icons', slots: ['appleTouchIcon', 'maskableIcon'] },
  { key: 'onboarding', label: 'Onboarding', slots: ['onboardingLight', 'onboardingDark'] },
  { key: 'marketing', label: 'Marketing', slots: ['emailHeader', 'socialImage'] },
];

const ASSET_MAX_BYTES = 5 * 1024 * 1024;

const SVG = 'image/svg+xml';
const PNG = 'image/png';
const JPEG = 'image/jpeg';

/** The slot's formats (#962 §3.5), as the file input's `accept`. */
const ACCEPT: Record<BrandAssetSlot, string> = {
  logoReversed: `${SVG},${PNG}`,
  mark: `${SVG},${PNG}`,
  faviconSvg: SVG,
  appleTouchIcon: PNG,
  maskableIcon: PNG,
  onboardingLight: `${SVG},${PNG}`,
  onboardingDark: `${SVG},${PNG}`,
  emailHeader: PNG,
  socialImage: `${PNG},${JPEG}`,
};

/** English fallbacks; the catalog copy lives under `brands.assetSlots.*`. */
const LABELS: Record<BrandAssetSlot, string> = {
  logoReversed: 'Reversed logo',
  mark: 'Mark',
  faviconSvg: 'SVG favicon',
  appleTouchIcon: 'Apple touch icon',
  maskableIcon: 'Maskable icon',
  onboardingLight: 'Onboarding (light)',
  onboardingDark: 'Onboarding (dark)',
  emailHeader: 'Email header',
  socialImage: 'Social image',
};

const RULES: Record<BrandAssetSlot, string> = {
  logoReversed: 'SVG or PNG. PNG at least 480×128, transparent.',
  mark: 'SVG or PNG, square. PNG at least 256×256, transparent.',
  faviconSvg: 'SVG with a square viewBox.',
  appleTouchIcon: 'PNG, exactly 180×180, opaque.',
  maskableIcon: 'PNG, exactly 512×512. Keep the artwork inside the circle.',
  onboardingLight: 'SVG or PNG, at most 2400 px on the long side.',
  onboardingDark: 'SVG or PNG, at most 2400 px on the long side.',
  emailHeader: 'PNG, exactly 600×120.',
  socialImage: 'PNG or JPEG, exactly 1200×630.',
};

/** Contract §3 `resolved.assets` → each slot's current URL. */
function slotUrls(assets: ResolvedAssets | undefined): SlotUrls {
  return {
    logoReversed: assets?.logoReversed ?? null,
    mark: assets?.mark ?? null,
    faviconSvg: assets?.favicon?.svg ?? null,
    appleTouchIcon: assets?.appleTouchIcon ?? null,
    maskableIcon: assets?.maskableIcon ?? null,
    onboardingLight: assets?.onboardingIllustration?.light ?? null,
    onboardingDark: assets?.onboardingIllustration?.dark ?? null,
    emailHeader: assets?.emailHeader ?? null,
    socialImage: assets?.socialImage ?? null,
  };
}

interface BrandAssetSlotsProps {
  brandId: number;
  /** Called after an upload or clear: the brand changed, so the Theme tab is stale. */
  onChanged: () => void;
}

export function BrandAssetSlots({ brandId, onChanged }: BrandAssetSlotsProps) {
  const { t } = useCopy();
  const [urls, setUrls] = useState<SlotUrls | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [pending, setPending] = useState<BrandAssetSlot | null>(null);
  const [errors, setErrors] = useState<Partial<Record<BrandAssetSlot, string>>>({});
  const [confirmSlot, setConfirmSlot] = useState<BrandAssetSlot | null>(null);

  useEffect(() => {
    let ignore = false;
    apiAdmin.brandTheme.get(brandId).then(
      (res) => {
        if (ignore) return;
        setLoadError(false);
        setUrls(slotUrls(res.resolved.assets));
      },
      () => {
        if (!ignore) setLoadError(true);
      },
    );
    return () => {
      ignore = true;
    };
  }, [brandId, reloadKey]);

  const label = (slot: BrandAssetSlot) => t(`brands.assetSlots.slots.${slot}`, LABELS[slot]);

  const tooLargeText = () => t('brands.assetSlots.tooLarge', 'File is over 5 MB.');

  function errorText(err: unknown): string {
    if (err instanceof ApiError) {
      if (err.code === 'ASSET_TOO_LARGE' || err.status === 413) return tooLargeText();
      if (err.status === 429) {
        return t('brands.assetSlots.rateLimited', 'Too many requests — try again in {{n}} s', {
          n: err.details.retryAfterSeconds ?? DEFAULT_RETRY_AFTER_SECONDS,
        });
      }
      if (err.details.message) {
        const reason =
          err.code === 'SVG_REJECTED'
            ? (err.details.raw as { error?: { reason?: unknown } } | undefined)?.error?.reason
            : undefined;
        return typeof reason === 'string' && reason.trim() !== ''
          ? `${err.details.message} — ${reason}`
          : err.details.message;
      }
      return t(`errors.codes.${err.code}`, t('errors.codes.UNKNOWN', 'Something went wrong.'));
    }
    return t('errors.codes.UNKNOWN', 'Something went wrong.');
  }

  async function run(slot: BrandAssetSlot, action: () => Promise<string | null>) {
    setPending(slot);
    setErrors((prev) => ({ ...prev, [slot]: undefined }));
    try {
      const url = await action();
      setUrls((prev) => (prev ? { ...prev, [slot]: url } : prev));
      onChanged();
    } catch (err) {
      setErrors((prev) => ({ ...prev, [slot]: errorText(err) }));
    } finally {
      setPending(null);
    }
  }

  function onPick(slot: BrandAssetSlot, e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    // Reset so picking the same file again still fires a change.
    e.target.value = '';
    if (!file) return;
    if (file.size > ASSET_MAX_BYTES) {
      setErrors((prev) => ({ ...prev, [slot]: tooLargeText() }));
      return;
    }
    void run(slot, async () => (await apiAdmin.brandAssets.upload(brandId, slot, file)).url);
  }

  function onConfirmClear() {
    const slot = confirmSlot;
    setConfirmSlot(null);
    if (!slot) return;
    void run(slot, async () => {
      await apiAdmin.brandAssets.clear(brandId, slot);
      return null;
    });
  }

  if (loadError && !urls) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={() => setReloadKey((n) => n + 1)}>
            {t('brands.assetSlots.reload', 'Reload')}
          </Button>
        }
      >
        {t('brands.assetSlots.loadFailed', "Couldn't load the brand images.")}
      </Alert>
    );
  }
  if (!urls) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress aria-label={t('brands.assetSlots.loading', 'Loading brand images')} />
      </Box>
    );
  }

  return (
    <Box component="section" aria-labelledby="brand-asset-slots-title">
      <Typography id="brand-asset-slots-title" variant="h3" gutterBottom>
        {t('brands.assetSlots.title', 'More brand images')}
      </Typography>
      <Stack spacing={3}>
        {GROUPS.map((group) => (
          <Box key={group.key}>
            <Typography variant="h4" gutterBottom>
              {t(`brands.assetSlots.groups.${group.key}`, group.label)}
            </Typography>
            <Grid container spacing={2}>
              {group.slots.map((slot) => (
                <Grid key={slot} size={{ xs: 12, md: 6, lg: 4 }}>
                  <SlotCard
                    slot={slot}
                    label={label(slot)}
                    rules={t(`brands.assetSlots.rules.${slot}`, RULES[slot])}
                    url={urls[slot]}
                    busy={pending === slot}
                    disabled={pending !== null}
                    error={errors[slot]}
                    onPick={(e) => onPick(slot, e)}
                    onClear={() => setConfirmSlot(slot)}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        ))}
      </Stack>
      <ConfirmDialog
        open={confirmSlot !== null}
        severity="error"
        title={
          confirmSlot
            ? t('brands.assetSlots.clearTitle', 'Clear {{label}}?', { label: label(confirmSlot) })
            : ''
        }
        body={
          confirmSlot
            ? t(
                'brands.assetSlots.clearBody',
                'The {{label}} image is removed from this brand. Apps fall back to their default.',
                { label: label(confirmSlot) },
              )
            : ''
        }
        confirmLabel={t('brands.assetSlots.clear', 'Clear')}
        onConfirm={onConfirmClear}
        onCancel={() => setConfirmSlot(null)}
      />
    </Box>
  );
}

interface SlotCardProps {
  slot: BrandAssetSlot;
  label: string;
  rules: string;
  url: string | null;
  busy: boolean;
  disabled: boolean;
  error: string | undefined;
  onPick: (e: ChangeEvent<HTMLInputElement>) => void;
  onClear: () => void;
}

function SlotCard({
  slot,
  label,
  rules,
  url,
  busy,
  disabled,
  error,
  onPick,
  onClear,
}: SlotCardProps) {
  const { t } = useCopy();
  return (
    <Paper variant="outlined" sx={{ p: 2 }} aria-label={label} role="group">
      <Stack spacing={1.5}>
        <Box>
          <Typography variant="subtitle2">{label}</Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {rules}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <PreviewTile
            tone="light"
            slot={slot}
            url={url}
            alt={t('brands.assetSlots.lightTile', '{{label}} on a light background', { label })}
          />
          <PreviewTile
            tone="dark"
            slot={slot}
            url={url}
            alt={t('brands.assetSlots.darkTile', '{{label}} on a dark background', { label })}
          />
        </Stack>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Button
            variant="outlined"
            size="small"
            component="label"
            disabled={disabled}
            aria-label={t('brands.assetSlots.replaceLabel', 'Replace {{label}}', { label })}
            startIcon={busy ? <CircularProgress size={14} /> : null}
          >
            {busy
              ? t('brands.assetSlots.uploading', 'Uploading…')
              : t('brands.assetSlots.replace', 'Replace')}
            <input type="file" hidden accept={ACCEPT[slot]} onChange={onPick} />
          </Button>
          <Button
            variant="text"
            size="small"
            color="error"
            disabled={disabled || !url}
            aria-label={t('brands.assetSlots.clearLabel', 'Clear {{label}}', { label })}
            onClick={onClear}
          >
            {t('brands.assetSlots.clear', 'Clear')}
          </Button>
        </Stack>
        {error ? <Alert severity="error">{error}</Alert> : null}
      </Stack>
    </Paper>
  );
}

interface PreviewTileProps {
  tone: 'light' | 'dark';
  slot: BrandAssetSlot;
  url: string | null;
  alt: string;
}

function PreviewTile({ tone, slot, url, alt }: PreviewTileProps) {
  const { t } = useCopy();
  const dark = tone === 'dark';
  return (
    <Box
      sx={{
        flex: 1,
        height: 96,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: dark ? 'grey.900' : 'common.white',
        color: dark ? 'grey.400' : 'grey.600',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'hidden',
      }}
    >
      {!url ? (
        <Typography variant="caption">{t('brands.assetSlots.notSet', 'Not set')}</Typography>
      ) : slot === 'maskableIcon' ? (
        <Box sx={{ position: 'relative', width: 80, height: 80, overflow: 'hidden' }}>
          <Box
            component="img"
            src={url}
            alt={alt}
            sx={{ width: '100%', height: '100%', display: 'block' }}
          />
          {/* The maskable safe zone: a centred circle, 80% of the icon's width. */}
          <Box
            role="img"
            aria-label={t('brands.assetSlots.safeZone', 'Maskable safe zone')}
            sx={{
              position: 'absolute',
              top: '10%',
              left: '10%',
              width: '80%',
              height: '80%',
              borderRadius: '50%',
              border: 1,
              borderStyle: 'dashed',
              borderColor: 'common.white',
              boxShadow: (theme) => `0 0 0 40px ${alpha(theme.palette.common.black, 0.5)}`,
            }}
          />
        </Box>
      ) : (
        <Box
          component="img"
          src={url}
          alt={alt}
          sx={{ maxWidth: '90%', maxHeight: '80%', display: 'block' }}
        />
      )}
    </Box>
  );
}
